import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { VirtualMachineSummary } from '../types/models';
import { ApiResponseVmList } from '../types/api/ApiResponseVirtualMachine';

interface UseVirtualMachinesReturn {
  vms: VirtualMachineSummary[];
  count: number;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useVirtualMachines = (limit = 25, offset = 0, name = '', role = ''): UseVirtualMachinesReturn => {
  const [vms, setVms] = useState<VirtualMachineSummary[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchVms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (name.trim()) {
        params.set('name', name.trim());
      }
      if (role.trim()) {
        params.set('role', role.trim());
      }

      const response = await apiFetch(`/datacenters/vms?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmList;
      if (data.status === 'success' && data.data?.vms) {
        setVms(data.data.vms);
        setCount(data.data.count);
      } else {
        setVms([]);
        setCount(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred while fetching virtual machines'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch, limit, offset, name, role]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchVms();
    }
  }, [auth.isAuthenticated, fetchVms]);

  return { vms, count, loading, error, refetch: fetchVms };
};
